import { useState } from 'react';
import { CheckCircle2, ChevronDown, ChevronUp, Loader2, XCircle, UploadCloud } from 'lucide-react';
import { useUIStore, type UploadMonitorItem } from '@/stores/ui';
import { cn } from '@/lib/cn';

type Tab = 'All' | 'Completed' | 'Failed';

function FileStatusIcon({ item }: { item: UploadMonitorItem }) {
  if (item.status === 'done') return <CheckCircle2 size={16} className="text-emerald-400 shrink-0" />;
  if (item.status === 'error') return <XCircle size={16} className="text-red-400 shrink-0" />;
  if (item.status === 'uploading') return <Loader2 size={16} className="text-[#00CBDE] shrink-0 animate-spin" />;
  return (
    <svg className="shrink-0" width="16" height="16" viewBox="0 0 16 16" fill="none">
      <circle cx="8" cy="8" r="7" stroke="#676687" strokeWidth="1.5" />
    </svg>
  );
}

export function UploadMonitor() {
  const items = useUIStore((s) => s.uploadItems);
  const open = useUIStore((s) => s.uploadMonitorOpen);
  const collapsed = useUIStore((s) => s.uploadMonitorCollapsed);
  const setCollapsed = useUIStore((s) => s.setUploadMonitorCollapsed);
  const clearItems = useUIStore((s) => s.clearUploadItems);
  const [tab, setTab] = useState<Tab>('All');

  if (!open && items.length === 0) return null;

  const activeCount = items.filter((i) => i.status === 'uploading' || i.status === 'waiting').length;
  const doneCount = items.filter((i) => i.status === 'done').length;
  const failedCount = items.filter((i) => i.status === 'error').length;
  const allDone = items.length > 0 && activeCount === 0;

  const visibleItems = items.filter((i) => {
    if (tab === 'Completed') return i.status === 'done';
    if (tab === 'Failed') return i.status === 'error';
    return true;
  });

  // Group items by folder
  const byFolder = new Map<string, { folderName: string; items: UploadMonitorItem[] }>();
  for (const item of visibleItems) {
    const key = item.folderId;
    if (!byFolder.has(key)) byFolder.set(key, { folderName: item.folderName, items: [] });
    byFolder.get(key)!.items.push(item);
  }

  return (
    <div className="fixed bottom-6 right-6 z-50 w-80 rounded-xl bg-[#12122C] shadow-2xl border border-white/10 overflow-hidden flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
        <div className="flex items-center gap-2">
          <UploadCloud size={16} className="text-white/60" />
          <span className="text-sm font-semibold text-white">Uploads</span>
        </div>
        <div className="flex items-center gap-2">
          {allDone && (
            <button
              onClick={clearItems}
              className="text-[11px] text-white/50 hover:text-white/80 transition-colors"
            >
              Clear
            </button>
          )}
          <button
            onClick={() => setCollapsed(!collapsed)}
            className="p-1 text-white/50 hover:text-white/80 transition-colors rounded"
          >
            {collapsed ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          </button>
        </div>
      </div>

      {!collapsed && (
        <>
          {/* Tabs */}
          <div className="flex items-center gap-1 px-3 pt-2 pb-1">
            {(['All', 'Completed', 'Failed'] as Tab[]).map((t) => (
              <button
                key={t}
                onClick={() => setTab(t)}
                className={cn(
                  'px-2.5 py-1 rounded-full text-[11px] font-medium transition-colors',
                  tab === t
                    ? 'bg-white/20 text-white'
                    : 'text-white/50 hover:text-white/80',
                )}
              >
                {t}
                {t === 'Failed' && failedCount > 0 && (
                  <span className="ml-1 text-red-400">{failedCount}</span>
                )}
              </button>
            ))}
          </div>

          {/* File list */}
          <div className="max-h-60 overflow-y-auto px-3 pb-2 space-y-1 custom-scrollbar">
            {byFolder.size === 0 ? (
              <p className="text-[11px] text-white/40 px-1 py-2">No uploads to show.</p>
            ) : (
              [...byFolder.entries()].map(([folderId, group]) => (
                <div key={folderId}>
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-white/40 mt-2 mb-1 px-1">
                    Uploading to <span className="text-white/60">{group.folderName || 'folder'}</span>
                  </p>
                  {group.items.map((item) => (
                    <div key={item.id} className="flex items-start gap-2 py-1.5">
                      <FileStatusIcon item={item} />
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-white truncate">{item.fileName}</p>
                        <p className="text-[10px] text-white/40 mt-0.5">
                          {item.status === 'waiting' && 'Waiting…'}
                          {item.status === 'uploading' && (item.percent >= 100 ? 'Finalizing…' : `${item.percent}%`)}
                          {item.status === 'done' && 'Uploaded'}
                          {item.status === 'error' && (item.error ?? 'Failed')}
                        </p>
                        {item.status === 'uploading' && (
                          <div className="h-0.5 bg-white/10 rounded-full mt-1 overflow-hidden">
                            <div
                              className="h-full bg-[#00CBDE] transition-[width]"
                              style={{ width: `${item.percent}%` }}
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ))
            )}
          </div>
        </>
      )}

      {/* Footer */}
      <div className={cn(
        'px-4 py-2.5 flex items-center gap-2',
        allDone ? 'bg-emerald-800/40' : 'bg-[#0410BD]',
      )}>
        {allDone ? (
          <>
            <CheckCircle2 size={15} className="text-emerald-400 shrink-0" />
            <span className="text-xs font-semibold text-white flex-1">
              {doneCount} upload{doneCount !== 1 ? 's' : ''} complete
              {failedCount > 0 && `, ${failedCount} failed`}
            </span>
          </>
        ) : (
          <>
            <UploadCloud size={15} className="text-white/80 shrink-0 animate-pulse" />
            <span className="text-xs font-semibold text-white flex-1">
              Uploading {activeCount} of {items.length} item{items.length !== 1 ? 's' : ''}
            </span>
          </>
        )}
      </div>
    </div>
  );
}
